/**
 * @file CopyManager.js
 * @fileOverview Localized text utility
 * @author Smartmatic
 * @creationDate 2019-06-13
 * @modificationDate 2025-03-14
 */
import get from 'lodash/get';
import { IS_DEV } from './process';

/**
 * Copies the input data based on the provided mode and language.
 * This function provides a convenient way to get a copy of the original data, either in default
 * or specified mode and language. Development (DEV) and production environments are differentiated by
 * the IS_DEV global variable. In DEV environment, the copy is in "ISB" mode by default. If this
 * mode is not supported, it falls back to English ('EN'). In production environment, the copy
 * language defaults to English ('EN') unless specified otherwise.
 *
 * @module Copy 
 * @exports module:Copy
 * @param {string} mode - The mode in which the copy should be made.
 * @param {string} language - The language of the intended copy, defaults to 'EN' if not specified.
 * @param {object} copy - The original data to be copied.
 * @returns {object} A copy of the original data, either in specified mode and language,
 * or default mode ('ISB' for DEV, 'EN' for production) and language ('EN').
 */
export default function Copy(mode, language, copy) {
  if (IS_DEV) {
    return get(copy, `${mode || "ISB"}.${language || 'EN'}`, {});
  }
  return get(copy, `${language || 'EN'}`, {});
}
